import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

const SESSION_KEY = 'pg_device_fingerprint_v1';

@Injectable({ providedIn: 'root' })
export class DeviceFingerprintService {
  readonly #document = inject(DOCUMENT);
  #cachedFingerprint: string | null = null;
  #inFlightFingerprint: Promise<string | null> | null = null;

  public async getFingerprint(useSessionStorage = true): Promise<string | null> {
    if (this.#cachedFingerprint) {
      return this.#cachedFingerprint;
    }

    const view = this.#document.defaultView;
    if (!view) {
      return null;
    }

    if (useSessionStorage) {
      try {
        const stored = view.sessionStorage.getItem(SESSION_KEY);
        if (stored) {
          this.#cachedFingerprint = stored;
          return stored;
        }
      } catch {
        // ignore storage errors
      }
    }

    if (this.#inFlightFingerprint) {
      return this.#inFlightFingerprint;
    }

    this.#inFlightFingerprint = (async () => {
      const rawFingerprintSource = this.#buildRawFingerprintSource(view);
      let fingerprint: string;

      try {
        fingerprint = await this.#sha256Hex(view, rawFingerprintSource);
      } catch {
        fingerprint = `fallback-${this.#fnv1aHex(rawFingerprintSource)}`;
      }

      this.#cachedFingerprint = fingerprint;

      if (useSessionStorage) {
        try {
          view.sessionStorage.setItem(SESSION_KEY, fingerprint);
        } catch {
          // ignore storage errors
        }
      }

      return fingerprint;
    })();

    try {
      return await this.#inFlightFingerprint;
    } finally {
      this.#inFlightFingerprint = null;
    }
  }

  #normalize(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  #getWebGLPart(view: Window): string {
    try {
      const canvas = view.document.createElement('canvas');
      const webglContext =
        (canvas.getContext('webgl') as WebGLRenderingContext | null) ||
        (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);

      if (!webglContext) {
        return 'webgl:none';
      }

      const debugInfo = webglContext.getExtension('WEBGL_debug_renderer_info') as
        | { UNMASKED_VENDOR_WEBGL: number; UNMASKED_RENDERER_WEBGL: number }
        | null;

      const vendor = debugInfo
        ? webglContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : webglContext.getParameter(webglContext.VENDOR);
      const renderer = debugInfo
        ? webglContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : webglContext.getParameter(webglContext.RENDERER);

      return `webgl:${String(vendor)}|${String(renderer)}`;
    } catch {
      return 'webgl:error';
    }
  }

  #buildRawFingerprintSource(view: Window): string {
    const userAgent = this.#normalize(view.navigator.userAgent);
    const platform = this.#normalize(view.navigator.platform);
    const language = this.#normalize(view.navigator.language);
    const timezone = this.#normalize(Intl.DateTimeFormat().resolvedOptions().timeZone);

    const screenWidth = view.screen?.width ?? 0;
    const screenHeight = view.screen?.height ?? 0;
    const screenColorDepth = view.screen?.colorDepth ?? 0;
    const devicePixelRatio = Number(view.devicePixelRatio || 1).toFixed(2);

    return [
      'v1',
      `ua=${userAgent}`,
      `platform=${platform}`,
      `lang=${language}`,
      `tz=${timezone}`,
      `screen=${screenWidth}x${screenHeight}x${screenColorDepth}@${devicePixelRatio}`,
      `gpu=${this.#normalize(this.#getWebGLPart(view))}`,
    ].join('|');
  }

  async #sha256Hex(view: Window, input: string): Promise<string> {
    const subtle = view.crypto?.subtle;
    if (!subtle) {
      throw new Error('crypto.subtle is unavailable');
    }

    const data = new TextEncoder().encode(input);
    const digest = await subtle.digest('SHA-256', data);

    return [...new Uint8Array(digest)]
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  #fnv1aHex(input: string): string {
    let hash = 0x811c9dc5;

    for (let index = 0; index < input.length; index++) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 0x01000193);
    }

    return (hash >>> 0).toString(16).padStart(8, '0');
  }
}
